import { Component, HostListener, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, Event as RouterEvent, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
//import moment from 'moment-es6';
import { MomentService } from '../../../../../shared/moment/moment.service';

import { EventRead } from '../../../../../events/shared/event-read.model';
import { EventsService } from '../../../../../events/shared/events.service';
import { UserService } from '../../../../../users/shared/user.service';
import { EventSearchParams } from '../../../../../events/search/event-search-params.model';
import { NavigationService } from '../../../../../shared/navigation/navigation.service';
import { AsyncState } from '../../../../../shared/model/index';
import { WlEventsToolbarBroadcastService, WlEventsToolbarEventTypeEnum, WlEventsToolbarEvent } from './broadcast/index';
import { RegistrationWizardComponent } from '../../../../../events/registration/wizard/registration-wizard.component';
import { WlEventDeleteModalComponent } from '../../../../../events/shared/delete-event-modal/delete-event-modal.component';
import { UtilService } from 'src/app/shared/util/util.service';

/**
 * Toolbar that goes at the top of the app.
 * This toolbar is for events.
 */
@Component({
  selector: 'wl-module-events-toolbar',
  templateUrl: 'toolbar-module-events.component.html',
  styleUrls: ['toolbar-module-events.component.css'],
  providers: [EventsService],
    standalone: false
})
export class WlModuleEventsToolbarComponent implements OnInit, OnDestroy {
  public static _storedKeyword: string = '';
  private _momentService: MomentService;

  public get keyword(): string {
    return WlModuleEventsToolbarComponent._storedKeyword;
  }
  public set keyword(value: string) {
    WlModuleEventsToolbarComponent._storedKeyword = value;
  }

  public _userEventsAccessLevel: number = 0;
  public _modelChangeDebounceTimeoutId: number = 0;
  public _inlineSearching: AsyncState = new AsyncState();
  public _searching: AsyncState = new AsyncState();
  public _inlineSearchResults: Array<any> = [];
  public _resultsOpen: boolean = false;
  public _isEventSearchPage: boolean = false;
  public _subscriptions: Array<Subscription> = [];
  public _enableSelectEventModal: boolean = false;
  public _enableNewRegistrationModal: boolean = false;
  public _enableDeleteEventModal: boolean = false;
  public _cancelLastInlineSearch: boolean = false;
  public _deleteEventId: number = 0;
  public showModal = false;

  @ViewChild('registrationWizardModal', { static: false }) public _registrationWizardModal: RegistrationWizardComponent;
  @ViewChild('deleteEventModal', { static: false }) public _deleteEventModal: WlEventDeleteModalComponent;

  /**
   * injection
   * @param _router
   * @param _route
   * @param _eventsService
   * @param _userService
   * @param _navigationService
   * @param _eventsToolbarBroadcastService
   */
  public constructor(
    public _router: Router,
    public _route: ActivatedRoute,
    public _eventsService: EventsService,
    public _userService: UserService,
    public _navigationService: NavigationService,
    public _eventsToolbarBroadcastService: WlEventsToolbarBroadcastService,
    momentService: MomentService,
    private _utilService: UtilService
  ) {
    this._momentService = momentService;
  }

  /**
   * setup
   */
  public ngOnInit(): void {
    // get this user's permissions
    this._userService.getCurrentUserSecurityElementAccessLevel('Events_Module').then((userAccessLevel: number) => {
      this._userEventsAccessLevel = userAccessLevel;
    });

    // initial load: to see if we are on the event advanced search page
    this._isEventSearchPage = this.isEventSearchUrl(this._router.url);

    // watch to see if we are on the event advanced search page
    this._subscriptions.push(
      this._router.events.subscribe((event: RouterEvent) => {
        if (!(event instanceof NavigationEnd)) return;

        let navigationEndEvent: NavigationEnd = <NavigationEnd>(<any>event);
        let url: string = navigationEndEvent.urlAfterRedirects == null ? '' : navigationEndEvent.urlAfterRedirects.toLocaleLowerCase();

        this._isEventSearchPage = this.isEventSearchUrl(url);
      })
    );

    // watch the query param for events
    this._subscriptions.push(
      this._route.queryParams.subscribe((params: any) => {
        if (this._isEventSearchPage && params && params.q) this.keyword = params.q;
      })
    );

    // watch for events sent to the toolbar
    this._subscriptions.push(
      this._eventsToolbarBroadcastService.subscribe((event: WlEventsToolbarEvent) => {
        // started searching/stopped searching
        if (event.type == WlEventsToolbarEventTypeEnum.searching) this._searching.busy = <boolean>event.data;
        // requested new registration
        else if (event.type == WlEventsToolbarEventTypeEnum.newRegistration) this.openEventRegistrationForEventId(<number>event.data);
        // requested to edit registration
        else if (event.type == WlEventsToolbarEventTypeEnum.editRegistration)
          this.openEventRegistrationForEventId(<number>event.data.EventId, <number>event.data.RegistrationId);
        else if (event.type == WlEventsToolbarEventTypeEnum.editAttendee)
          this.openEventRegistrationForEventId(
            <number>event.data.EventId,
            <number>event.data.RegistrationId,
            <number>event.data.AttendeeId
          );
        // requested to delete an event
        else if (event.type == WlEventsToolbarEventTypeEnum.deleteEvent) this.openDeleteEventModal(<number>event.data);
        // some component (that is not the parent) needs the keyword
        else if (event.type == WlEventsToolbarEventTypeEnum.requestKeyword)
          this._eventsToolbarBroadcastService.broadcast({ type: WlEventsToolbarEventTypeEnum.requestKeywordAnswer, data: this.keyword });
      })
    );
  }

  /**
   * clean up
   */
  public ngOnDestroy(): void {
    // clean up subscriptions
    if (this._subscriptions) this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * do an inline search
   */
  public inlineSearch(): void {
    this._inlineSearching.busy = true;
    this._inlineSearching.success = false;
    this._inlineSearching.message = '';

    // keep track of requests in case they come back really fast
    let currentRequestId: number = ++this._inlineSearching.requestId;

    // only get next most recent
    let searchParams: EventSearchParams = new EventSearchParams();
    searchParams.PageSize = 5;
    searchParams.OrderBy = 'StartDateTimeUTC:desc';
    searchParams.OrganizationEvent = false;
    searchParams.CommunityEvent = false;
    searchParams.MembersOnlyEvent = false;
    searchParams.InternalEvent = false;

    if (/^\d+$/.test(this.keyword)) searchParams.EventId = parseInt(this.keyword);
    else searchParams.Keyword = this.keyword;

    searchParams.SearchDateBegin = this._momentService
      .getMoment()
      .subtract(1, 'years')
      .startOf('day')
      .toDate();
    searchParams.SearchDateEnd = this._momentService
      .getMoment()
      .add(1, 'years')
      .startOf('day')
      .toDate();

    this._cancelLastInlineSearch = false;

    this._eventsService.searchEvents(searchParams).then(
      (result: any) => {
        if (currentRequestId != this._inlineSearching.requestId) return;

        this._inlineSearching.busy = false;
        this._inlineSearching.success = true;
        this._inlineSearching.message = '';

        this._inlineSearchResults = result && result.Result ? result.Result : [];

        if (!this._cancelLastInlineSearch) this.openResults();

        // reset
        this._cancelLastInlineSearch = false;
      },
      (error: any) => {
        if (currentRequestId != this._inlineSearching.requestId) return;

        this._inlineSearching.busy = false;
        this._inlineSearching.success = false;
        this._inlineSearching.message = this._utilService.extractErrorMessage(error);

        if (!this._cancelLastInlineSearch) this.openResults();

        // reset
        this._cancelLastInlineSearch = false;
      }
    );
  }

  /**
   * open the result panel
   */
  public openResults(): void {
    this._resultsOpen = true;
  }

  /**
   * close the result panel
   */
  public closeResults(): void {
    this._resultsOpen = false;
  }

  /**
   * enable and open the select event modal
   */
  public openSelectEventModal(): void {
    // not allowed
    if (this._userEventsAccessLevel < 1) return;

    this._enableSelectEventModal = true;
    setTimeout(() => {
      this.showModal = true;
    });
  }

  /**
   * close the select event modal
   */
  public closeSelectEventModal(): void {
    this.showModal = false;
    this.handleSelectEventModalCloseComplete();
  }

  /**
   * opens the event registration like normal, but loads the event for you first
   * @param eventId
   * @param registrationId (optional. Required if attendee id given)
   * @param attendeeId (optional)
   */
  public openEventRegistrationForEventId(eventId: number, registrationId: number = 0, attendeeId: number = 0): void {
    // not allowed
    if (this._userEventsAccessLevel < 1) return;

    this._enableNewRegistrationModal = true;

    setTimeout(() => this._registrationWizardModal.openEventRegistrationForEventId(eventId, registrationId, attendeeId));
  }

  /**
   * opens the modal to take you through event deletion process
   * @param eventId
   */
  public openDeleteEventModal(eventId: number): void {
    // not allowed
    if (this._userEventsAccessLevel < 2) return;

    this._enableDeleteEventModal = true;

    setTimeout(() => this._deleteEventModal.open(eventId));
  }

  /**
   * helper method that determines if the given url is the events dashboard url
   * @param testUrl
   */
  public isEventSearchUrl(testUrl: string) {
    testUrl = testUrl.toLowerCase();
    return testUrl.startsWith('/events/') || testUrl.startsWith('/events?') || testUrl == '/events';
  }

  /**
   * triggered by submitting the search form
   */
  public handleSearchFormSubmit(): void {
    // go to advanced search
    this._navigationService.navigateToEventsHome({ q: this.keyword, _r: new Date().getTime() });

    clearTimeout(this._modelChangeDebounceTimeoutId);
    this._cancelLastInlineSearch = true;

    this.closeResults();
  }

  /**
   * triggered by clicking the reset button
   */
  public handleResetClick(): void {
    this.keyword = '';

    if (this._isEventSearchPage) this._navigationService.navigateToEventsHome({ q: this.keyword, _r: new Date().getTime() });

    this._eventsToolbarBroadcastService.broadcast(new WlEventsToolbarEvent(WlEventsToolbarEventTypeEnum.clearKeyword));
  }

  /**
   * triggerd on model change
   */
  public handleModelChange(): void {
    clearTimeout(this._modelChangeDebounceTimeoutId);

    if (this.keyword && (this.keyword.trim().length > 2 || /^\d+$/.test(this.keyword)))
      this._modelChangeDebounceTimeoutId = window.setTimeout(() => this.inlineSearch(), 550);
  }

  /**
   * triggerd by new event button click
   */
  public handleNewEventClick(): void {
    // has access?
    if (this._userEventsAccessLevel >= 2)
      this._eventsToolbarBroadcastService.broadcast(new WlEventsToolbarEvent(WlEventsToolbarEventTypeEnum.newEvent));
  }

  /**
   * when the select event modal finishes closing, re-disable it
   */
  public handleSelectEventModalCloseComplete(): void {
    this._enableSelectEventModal = false;
  }

  /**
   * triggered when user selects an event from the event selector
   * @param selectedEvent
   */
  public handleNewRegistrationEventSelect(selectedEvent: EventRead): void {
    // close event selector modal
    this.showModal = false;
    if (selectedEvent) this.openEventRegistrationForEventId(selectedEvent.EventId);
  }

  /**
   * triggered when the new registration is saved
   * @param registrationId
   */
  public handleRegistrationSaved(registrationId: number) {
    this._eventsToolbarBroadcastService.broadcast({ type: WlEventsToolbarEventTypeEnum.registrationSaved, data: registrationId });
  }

  /**
   * triggered when the registration wizzard window is completely closed
   */
  public handleRegistrationClosed() {
    this._enableNewRegistrationModal = false;
    this._eventsToolbarBroadcastService.broadcast(new WlEventsToolbarEvent(WlEventsToolbarEventTypeEnum.registrationClosed));
  }

  /**
   * triggered when the delete modal is finished closing
   * @param eventDeleted
   */
  public handleDeleteModalClosed(eventDeleted: boolean) {
    setTimeout(() => {
      // decomission
      this._enableDeleteEventModal = false;

      // if the event was deleted, go to event dashboard
      if (eventDeleted) this._navigationService.navigateToEventsHome({ q: this.keyword, _r: new Date().getTime() });
    });
  }

  /**
   * handle document clicks
   */
  @HostListener('document:click')
  public handleDocumentClick() {
    this.closeResults();
  }
}
